import React, { useState, useEffect } from 'react';
import './MainInterface.css';
import Logo from './Logo';

const MainInterface = ({ onQuestionSelect, onExitSession, ageToggle, onAgeToggle, voice, onVoiceChange  }) => {
  const [showFeaturedQuestions, setShowFeaturedQuestions] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  // Set consistent background for entire page
  useEffect(() => {
    document.body.style.background = 'var(--color-bg)';
    document.body.style.backgroundAttachment = 'fixed';
    
    return () => {
      // Cleanup on unmount
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
    };
  }, []);

  const featuredQuestions = [
    "What happens during puberty?",
    "How long after unprotected sex can I know if I'm really pregnant?", 
    "What does it mean if my period is late?",
    "How do I book a sexual health appointment?",
    "What is the right age to have sex for the first time?",
    "How do you clean your vagina?",
    "Does watching gay porn mean you're gay?",
    "Can you get pregnant if your partner pulls out?",
    "What will happen to me if I masturbate too much?",
    "How much does the abortion pill cost?"
  ];

  const allTopics = [
    {
      icon: "🌱",
      title: "Puberty & Development",
      description: "Body changes, growth, hormones"
    },
    {
      icon: "💊",
      title: "Birth Control",
      description: "Contraception options and effectiveness"
    },
    {
      icon: "👥",
      title: "Bodies & Anatomy",
      description: "Sexual anatomy and body functions"
    },
    {
      icon: "🏥",
      title: "Health Services & Cost",
      description: "Healthcare access and pricing"
    },
    {
      icon: "💭",
      title: "Masturbation",
      description: "Self-pleasure and sexual wellness"
    },
    {
      icon: "🤰",
      title: "Pregnancy",
      description: "Conception, testing, and options"
    },
    {
      icon: "❤️",
      title: "Relationships",
      description: "Dating, communication, and consent"
    },
    {
      icon: "🍑",
      title: "Sex",
      description: "Sexual activity and safety"
    },
    {
      icon: "🏳️‍🌈",
      title: "Sexual Orientation & Gender",
      description: "Identity and self-discovery"
    },
    {
      icon: "🩺",
      title: "Symptoms & STIs",
      description: "Sexual health concerns and testing"
    },
    {
      icon: "⚖️",
      title: "Pregnancy Options",
      description: "Options, procedures, and support"
    },
    {
      icon: "🩸",
      title: "Periods & Menstruation",
      description: "Menstrual health and cycles"
    }
  ];

  const handleInputFocus = () => {
    setInputFocused(true);
    setShowFeaturedQuestions(true);
    setShowAllTopics(false);
    setShowFaq(false);
    setShowHelp(false);
    
    // Check if mobile
    const isMobile = window.innerWidth <= 768;
    
    // Scroll behavior - different for mobile and desktop
    setTimeout(() => {
      if (isMobile) {
        // For mobile: scroll to input section, not to bottom
        const inputSection = document.querySelector('.input-section');
        if (inputSection) {
          inputSection.scrollIntoView({
            behavior: 'smooth',
            block: 'center' // Center the input section in viewport
          });
        }
      } else {
        // For desktop: scroll to bottom as before
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleInputBlur = () => {
    // Delay hiding to allow clicking on questions
    setTimeout(() => {
      setInputFocused(false);
      setShowFeaturedQuestions(false);
    }, 200);
  };

  const handleQuestionClick = (question) => {
    setShowFeaturedQuestions(false);
    setShowAllTopics(false);
    setShowFaq(false);
    setShowHelp(false);
    onQuestionSelect(question);
  };

  const handleAllTopicsClick = () => {
    setShowFeaturedQuestions(false);
    setShowAllTopics(true);
    setShowFaq(false);
    setShowHelp(false);
  };

  const handleFaqClick = () => {
    setShowFeaturedQuestions(false);
    setShowAllTopics(false);
    setShowFaq(true);
    setShowHelp(false);
  };

  const handleHelpClick = () => {
    setShowFeaturedQuestions(false);
    setShowAllTopics(false);
    setShowFaq(false);
    setShowHelp(true);
  };

  const handleTopicClick = (topic) => {
    const topicQuestion = `I want to learn about ${topic.title.toLowerCase()}`;
    handleQuestionClick(topicQuestion);
  };

  return (
    <div className="main-interface">
      {/* Header */}
      <div className="main-header" style={{color: '#133153'}}>
        <button 
          className="exit-button"
          onClick={onExitSession}
          title="Exit Session"
        >
          ← Exit
        </button>
        <h1 className="main-title" style={{ 
          color: '#133153',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          margin: 0
        }}>
          <span style={{ marginRight: '0.5rem' }}>
            <Logo size="1.2rem" />
          </span>
          S.U.E.
        </h1>

        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          color: '#133153'
        }}>
          {/* Drop down selection for the voice options */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'white',
            fontSize: '12px'
          }}>
            <label style={{ color: '#133153' }}>S.U.E. Style: </label>
            <select
              value={voice}
              onChange={onVoiceChange}
              className="voice-select"
            >
              <option value="base">Base S.U.E. (default, educator)</option>
              <option value="direct">Direct S.U.E. (clear and efficient)</option>
              <option value="warm">Peer S.U.E. (relatable and reassuring)</option>
            </select>
          </div>

          {/* Age toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'white',
            fontSize: '12px'
          }}>
            <span style={{ color: '#133153' }}>Primary Mode</span>
            <div
              onClick={onAgeToggle}
              style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                background: ageToggle ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s ease',
                flexShrink: 0
              }}
            >
              <div style={{
                position: 'absolute',
                top: '3px',
                left: ageToggle ? '23px' : '3px',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: ageToggle ? 'var(--color-dark-green)' : 'white',
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Welcome Section */}
        <div className="welcome-section">
          <div className="avatar">
            <Logo size="3rem" className="avatar-emoji" />
          </div>
          <h2 className="welcome-title">
            Hey there, I'm S.U.E., here to answer your sexual health questions!
          </h2>
          <p className="welcome-subtitle-below">
            I am in <strong>{ageToggle ? 'Primary' : 'Secondary'}</strong> Mode and <strong>{voice === 'base' ? 'Base S.U.E.' : voice === 'direct' ? 'Direct S.U.E.' : 'Peer S.U.E.'}</strong> style!
          </p>
        </div>

        {/* Privacy Notice */}
        <div className="privacy-notice-detailed" style={{
          background: 'linear-gradient(135deg, #bada55 0%,  #f4ffa9 100%)',
          color: 'white',
          padding: '1rem',
          borderRadius: '15px',
          marginBottom: '1rem',
          boxShadow: '0 4px 15px rgba(66, 106, 90, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', color: '#133153' }}>
            <span style={{ fontSize: '24px', marginRight: '0.5rem'}}>🔐</span>
            <strong>Private & Confidential Chat</strong>
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.5', color: '#133153'}}>
            <p style={{ margin: '0 0 0.5rem 0' }}>💬 I'm here to answer your questions about sexual health, consent, relationships, and more.</p>
            <p style={{ margin: '0 0 0.5rem 0' }}>📚 My answers come from S.U.E.: Sexual Health Education's trusted Canadian curriculum and public, evidence-based resources.</p>
            <p style={{ margin: '0 0 0.5rem 0' }}>🐣 I'm now equipped to answer questions for grades 4-7: toggle on <strong>primary mode</strong>!</p>
            <p style={{ margin: '0 0 0.5rem 0' }}>🗣️ I can also answer as an educator (base), directly, or as a peer. Check the options in the top corner!</p>
            <p style={{ margin: '0 0 0.5rem 0' }}>📊 We collect your questions anonymously to keep the tool accurate, safe, and inclusive.</p>
            <p style={{ margin: '0 0 0.5rem 0' }}>🔐 This chat is private and confidential, but it's not a health care portal — please don't share personal or identifying health info.</p>
            <p style={{ margin: '0' }}>👤 A human does not monitor this chat in real time. We review anonymized data to ensure safety and accuracy, but we cannot provide crisis or medical services. This tool is for general education only.</p>
          </div>
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="input-container">
            <input
              type="text"
              placeholder="Ask a question"
              className={`main-input ${inputFocused ? 'focused' : ''}`}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  handleQuestionClick(e.target.value.trim());
                  e.target.value = '';
                }
              }}
            />
          </div>

          {/* Featured Questions */}
          {showFeaturedQuestions && (
            <div className="featured-questions">
              <div className="featured-header">
                <span className="featured-label">FEATURED</span>
                <span className="featured-all" onClick={handleAllTopicsClick}>ALL TOPICS</span>
                <span className="featured-all" onClick={handleFaqClick}>FAQ</span>
                <span className="featured-help" onClick={handleHelpClick}>HELP</span>
              </div>
              <div className="questions-list">
                {featuredQuestions.map((question, index) => (
                  <div
                    key={index}
                    className="featured-question"
                    onClick={() => handleQuestionClick(question)}
                  >
                    {question}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Topics */}
          {showAllTopics && (
            <div className="featured-questions">
              <div className="featured-header">
                <span className="featured-all" onClick={() => setShowAllTopics(false)}>FEATURED</span>
                <span className="featured-label">ALL TOPICS</span>
                <span className="featured-all" onClick={handleFaqClick}>FAQ</span>
                <span className="featured-help" onClick={handleHelpClick}>HELP</span>
              </div>
              <div className="topics-grid">
                {allTopics.map((topic, index) => (
                  <div
                    key={index}
                    className="topic-card"
                    onClick={() => handleTopicClick(topic)}
                  >
                    <div className="topic-icon">{topic.icon}</div>
                    <div className="topic-content">
                      <div className="topic-title">{topic.title}</div>
                      <div className="topic-description">{topic.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {showFaq && (
            <div className="featured-questions">
              <div className="featured-header">
                <span className="featured-all" onClick={() => setShowFaq(false)}>FEATURED</span>
                <span className="featured-all" onClick={handleAllTopicsClick}>ALL TOPICS</span>
                <span className="featured-label">FAQ</span>
                <span className="featured-help" onClick={handleHelpClick}>HELP</span>
              </div>
              <div className="help-content">
                <div className="help-section" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <Logo size="3rem" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--color-dark-green)', margin: '0 0 0.5rem 0' }}>
                    Sex Ed Chatbot – Frequently Asked Questions
                  </h2>
                  <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', margin: '0 0 1rem 0' }}>
                    This chatbot was developed by The Whole SHEbang: Sexual Health Education to help increase access to accurate, inclusive, and student-centered sexual health information. Below are answers to common questions about how the tool was built, our ethical considerations, and how your data is protected.
                  </p>
                </div>
                
                <div className="help-section">
                  <h3>Is my data private?</h3>
                  <p>Yes. Your privacy matters. Any data shared with this chatbot is secure and will never be sold or shared outside of The Whole SHEbang: Sexual Health Education. We do not track personal identities, store chat histories long-term, or use your questions for marketing or advertising.</p>
                  <p>We do collect login details and the questions you ask so we can monitor for safety concerns and make sure the information provided is accurate, age-appropriate, and inclusive. This helps us improve the tool while keeping your wellbeing at the center of everything we do.</p>
                </div>
                
                <div className="help-section">
                  <h3>Where do the answers come from?</h3>
                  <p>This tool was built using real, anonymous questions from Canadian students, along with Carlie McPhee's original, copyrighted curriculum. The curriculum is rooted in evidence-based resources, sound teaching theory, and publicly available sexual health frameworks used across Canada.</p>
                  <p>No private, copyrighted, or creative work from other individuals or organizations was used to train this chatbot. The content reflects The Whole SHEbang's commitment to trustworthy, age-appropriate, inclusive, and trauma-informed sex ed.</p>
                </div>

                <div className="help-section">
                  <h3>What about the environmental impact of AI?</h3>
                  <p>We recognize that AI tools carry an environmental cost, including the use of land, energy, and water resources. While this tool was built to increase access to inclusive sex ed, we know that digital tools are not impact-free.</p>
                  <p>As more environmentally sustainable technologies become available, we are committed to reassessing and adapting our systems to reduce harm and promote more sustainable options.</p>
                </div>

                <div className="help-section">
                  <h3>Is this tool meant to replace sex educators?</h3>
                  <p>Not at all. This chatbot is not intended to replace human educators, but to fill a documented gap in Canadian sexual health education. Many communities face barriers to accessing accurate, inclusive, and engaging resources — and this tool exists to help close that gap.</p>
                  <p>Our goal is to empower students, teachers, and parents by increasing knowledge, motivation, and confidence to support lifelong positive sexual health. This is a complement to — not a substitute for — the essential role of trained, caring educators.</p>
                </div>
              </div>
            </div>
          )}

          {/* Help Section */}
          {showHelp && (
            <div className="featured-questions">
              <div className="featured-header">
                <span className="featured-all" onClick={() => setShowHelp(false)}>FEATURED</span>
                <span className="featured-all" onClick={handleAllTopicsClick}>ALL TOPICS</span>
                <span className="featured-all" onClick={handleFaqClick}>FAQ</span>
                <span className="featured-label">HELP</span>
              </div>
              <div className="help-content">
                <div className="help-section" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <Logo size="3rem" />
                  </div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--color-dark-green)', margin: '0 0 0.5rem 0' }}>
                    S.U.E.
                  </h2>
                  <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', margin: '0 0 1rem 0' }}>
                    Sexuality Understanding & Education
                  </p>
                </div>
                
                <div className="help-section">
                  <h3>About this chatbot</h3>
                  <p>This is a confidential sexual health education chatbot designed to provide accurate, age-appropriate information about sexual health topics.</p>
                  <p>Generally this tool is recommended for Grade 7 and up.</p>
                </div>
                
                <div className="help-section">
                  <h3>How to use</h3>
                  <p>You can:</p>
                  <p>• Type your question in the search box</p>
                  <p>• Choose from featured questions</p>
                  <p>• Browse topics to explore different areas</p>
                  <br/>
                  <p>To keep your chat for later, you can download a PDF by hitting the PDF button in the top right corner.</p>
                  <p>To erase the chat, press the "🗑️ Clear" button in the top right corner.</p>
                </div>

                <div className="help-section">
                  <h3>Emergency Resources</h3>
                  <p>Kids Help Phone: 1-800-668-6868 | Text 686868 | kidshelpphone.ca</p>
                  <p>National LGBTQ Youth Talk Line: 1-800-246-7743</p>
                  <p>Trans Lifeline: 1-877-330-6366 | translifeline.org</p>
                  <p>1-800-SUICIDE: 1-800-784-2433</p>
                </div>

                <div className="help-section">
                  <h3>Need Additional Support?</h3>
                  <div className="contact-info">
                    <strong>Carlie McPhee</strong><br/>
                    Comprehensive Sexual Health Educator<br/>
                    carlie.mcphee@thewholeshebang.ca
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainInterface; 